"use client";

import { useEffect, useRef } from "react";
import {
  animate,
  motion,
  useInView,
  useMotionValue,
  useTransform,
} from "framer-motion";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Tag01Icon,
  UniversityIcon,
  PieChartIcon,
  StarCircleIcon,
} from "@hugeicons/core-free-icons";
import { EASE } from "@/lib/motion";

const stats = [
  { icon: Tag01Icon, value: 100, decimals: 0, suffix: " %", label: "Kostenlos", sublabel: "Keine versteckten Kosten" },
  { icon: UniversityIcon, value: 42, decimals: 0, suffix: "+", label: "Hochschulen", sublabel: "Alle öffentlichen Unis" },
  { icon: PieChartIcon, value: 3, decimals: 0, suffix: "", label: "Quoten", sublabel: "Abi · AdH · ZEQ" },
  { icon: StarCircleIcon, value: 4.8, decimals: 1, suffix: "", label: "Sterne im Store", sublabel: "1.200+ Bewertungen" },
];

function CountUp({
  target,
  decimals,
  suffix,
  started,
}: {
  target: number;
  decimals: number;
  suffix: string;
  started: boolean;
}) {
  const value = useMotionValue(0);
  const formatted = useTransform(value, (v) =>
    v.toLocaleString("de-DE", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    })
  );

  useEffect(() => {
    if (!started) return;
    const controls = animate(value, target, { duration: 1.4, ease: EASE });
    return () => controls.stop();
  }, [started, target, value]);

  return (
    <span className="tnum">
      <motion.span>{formatted}</motion.span>
      {suffix}
    </span>
  );
}

export default function StatsBar() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <div ref={ref} className="relative text-paper">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12 py-14 lg:py-16">
          {stats.map(({ icon: Icon, value, decimals, suffix, label, sublabel }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
              className="flex flex-col px-2 lg:px-6 text-center lg:text-left"
            >
              {/* <dl>'s <div> children must directly contain only <dt>/<dd> (WHATWG dl
                  content model) — the icon lives inside <dd>, not in a sibling wrapper,
                  so this stays a valid dt/dd group for a11y + agentic-browsing crawlers. */}
              <dt className="order-2 mt-3">
                <span className="block font-semibold text-sm">{label}</span>
                <span className="block text-sm text-paper/60 mt-0.5">{sublabel}</span>
              </dt>
              <dd className="order-1 flex items-center justify-center lg:justify-start gap-3 lg:gap-4 font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-none whitespace-nowrap">
                <HugeiconsIcon
                  icon={Icon}
                  size={40}
                  strokeWidth={1.5}
                  className="text-paper shrink-0"
                  aria-hidden="true"
                />
                {inView ? (
                  <CountUp target={value} decimals={decimals} suffix={suffix} started={inView} />
                ) : (
                  <span>{value.toLocaleString("de-DE", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>
                )}
              </dd>
            </motion.div>
          ))}
        </dl>
      </div>
    </div>
  );
}
